<template>
  <q-card
    class="col-auto column full-height noselect"
    :class="{
      'bg-primary': isSelected,
      'text-white': isSelected,
      'bg-green-1': hasReachedMinTrials,
    }"
  >
    <q-card-section
      v-if="hasDiscriminativeStimulus"
      class="bg-primary-variant text-white"
      :class="{
        'q-py-none': tiny,
        'q-py-xs': !tiny ,
        'cursor-pointer': targetIsOpen && (isTimerStarted || isSessionTimerLinked),
        'light-dimmed': (!isTimerStarted && !isSessionTimerLinked),
      }"
      @click="onSelectTarget"
    >
      <q-badge
        v-if="!!totalBadge"
        floating
        class="front-of-dimmed"
        :style="promptLevelStyle"
      >
        {{ totalBadge }}
      </q-badge>
      {{ discriminativeStimulus }}
    </q-card-section>
    <q-card-section
      v-touch-hold.mouse="onAddMultipleTrials"
      class="col row items-center"
      :class="{
        'text-overline text-uppercase text-dark-variant rate-data-overline q-pt-xs q-pb-none':
          tiny && !isSessionTimerLinked,
        'q-py-sm': dense && !isSessionTimerLinked,
        'q-py-md': dense && tiny && isSessionTimerLinked,
        'cursor-pointer': targetIsOpen && (isTimerStarted || isSessionTimerLinked),
        'light-dimmed': (!isTimerStarted && !isSessionTimerLinked),
      }"
      @click="onSelectTarget"
    >
      <q-badge
        v-if="!hasDiscriminativeStimulus && !!totalBadge"
        floating
        class="front-of-dimmed"
        :style="promptLevelStyle"
      >
        {{ totalBadge }}
      </q-badge>
      <div
        class="row items-center"
      >
        <q-chip
          v-if="!tiny"
          size="sm"
          :text-color="getTextColor(getTargetWorkflowProperty('color', ''))"
          :color="getTargetWorkflowProperty('color', '')"
          :style="{ backgroundColor: getTargetWorkflowProperty('color', '') }"
          :icon="getTargetWorkflowProperty('icon', 'help')"
          class="chip-no-label"
        />
        <span class="q-ml-xs">{{ target.label }}</span>
      </div>
      <q-menu
        v-if="showPromptLevelOptions"
        :anchor="promptLevelsMenuPosition"
        transition-show="none"
        transition-hide="none"
        auto-close
        @hide="isSelected = false"
      >
        <q-list style="min-width: 100px">
          <q-item
            v-for="promptLevel in programPrompts"
            :key="promptLevel.id"
            clickable
            :style="getPromptLevelStyle(promptLevel)"
            class="prompt-level"
            @click="onSelectPromptLevel(promptLevel)"
          >
            <q-item-section>{{ promptLevel.trialOutcome }}</q-item-section>
          </q-item>
        </q-list>
      </q-menu>
    </q-card-section>
    <q-card-section
      v-if="!isSessionTimerLinked"
      v-touch-hold.mouse="onAddMultipleTrials"
      class="text-center full-width text-subtitle2 text-white bg-primary-variant"
      :class="{
        'q-py-sm': dense && !tiny,
        'q-py-none': dense && tiny,
        'cursor-pointer': targetIsOpen,
        'light-dimmed': !targetIsOpen
      } "
      @mousedown="onMouseDown"
      @touchstart.prevent="onMouseDown"
      @mouseup="onMouseUp"
      @touchend.prevent="onMouseUp"
    >
      <template
        v-if="(stopIndicatorProgress === null) || (stopIndicatorProgress > 100)"
      >
        {{ trialBadge }}
      </template>
      <q-circular-progress
        v-else
        :value="stopIndicatorProgress"
        instant-feedback
        show-value
        track-color="transparent"
        class="text-white"
      >
        <q-icon
          name="stop"
          size="8px"
        />
      </q-circular-progress>
    </q-card-section>
  </q-card>
</template>
<script>
import AddMultipleTrials from 'components/Modals/AddMultipleTrials';
import { Timestamp } from 'firebase/firestore';
import { chain, get, head, isDate, isEmpty, size } from 'lodash';
import DataCollection from 'src/mixins/DataCollection';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { getTextColor } from 'src/utils/TargetUtils';

export default {
  name: 'RateData',

  mixins: [Targets, DataCollection, Toolbox, Interactions],

  emits: ['record', 'record-program-data'],

  data() {
    return {
      timerStart: null,
      stopIndicatorProgress: null,

      duration: 0,

      isSelected: false,

      timerInterval: null,
    };
  }, // data()

  computed: {

    isTimerStarted() {
      return (this.timerInterval !== null);
    }, // isTimerStarted()

    durationOffset() {
      const timerOffsetTimestamp = get(this.data, `timerOffset_${this.target.id}`);
      if (chain(timerOffsetTimestamp).get('toDate').isFunction().value()) {
        return timerOffsetTimestamp.toDate();
      }
      return null;
    },

    elapsedDuration() {
      return get(this.data, `timerDuration_${this.target.id}`) || 0;
    },

    currentTrial() {
      return chain(this.data)
        .get('data')
        .filter({ target: this.target.id })
        .maxBy('trial')
        .get('trial', 1)
        .value();
    }, // currentTrial()

    nextTrial() {
      return chain(this.data)
        .get('data')
        .maxBy('trial')
        .get('trial', 0)
        .add(1)
        .value();
    }, // currentTrial()

    promptLevelStyle() {
      return this.previousPromptLevelStyle;
    }, // promptLevelStyle()

    isCountDown() {
      return (get(this.target, 'timerType', 'up') === 'down');
    }, // isCountDown()

    isSessionTimerLinked() {
      return (get(this.target, 'timerType', 'up') === 'session');
    }, // isSessionTimerLinked()

    timerPreset() {
      return get(this.target, 'timerPreset', 0);
    },

    durationFormatted() {
      if (this.isCountDown) {
        return this.formatDuration(this.timerPreset - this.currentDuration, false);
      }
      return this.formatDuration(this.currentDuration, false);
    }, // durationFormatted()

    hasTotalDuration() {
      return this.totalDuration > 0;
    }, // hasTotalDuration()

    hasData() {
      return !chain(this.data.data)
        .filter({ target: this.target.id })
        .isEmpty()
        .value();
    }, // hasData()

    durationPromptLevelTiming() {
      return get(this.sessionProgram, ['program', 'durationPromptLevelTiming'], 'start');
    }, // durationPromptLevelTiming()

    showPromptLevelOptions() {
      return this.hasMultiplePromptLevels && this.targetIsOpen && (this.isTimerStarted || this.isSessionTimerLinked);
    }, // showPromptLevelOptions()

    promptLevelsMenuPosition() {
      if (this.$q.platform.is.ipad) {
        return 'bottom left';
      }

      return this.$q.screen.gt.sm ? 'top right' : undefined;
    }, // promptLevelsMenuPosition()

    currentDuration() {
      return this.elapsedDuration + this.duration;
    },

    pastDuration() {
      return chain(this.data)
        .get('data')
        .filter({ target: this.target.id })
        .sumBy('duration')
        .value() || 0;
    }, // pastDuration()

    totalDuration() {
      if (!get(this.target, 'id')
        || !get(this.data, 'data')) {
        return (this.currentDuration);
      }

      return this.pastDuration + this.elapsedDuration + this.duration;
    }, // totalDuration()

    trialDataInfo() {
      const trials = chain(this.data)
        .get('data')
        .filter({ target: get(this.target, 'id') })
        .filter({ trial: this.currentTrial })
        .filter('promptLevel')
        .value();

      const trialCount = size(trials);

      if (!this.hasMultiplePromptLevels) {
        return trialCount;
      }

      return this.calculateTrialsSummary(trials);
    }, // trialDataInfo()

    trialBadge() {
      if (this.currentDuration === 0) {
        if (this.isCountDown) {
          return this.formatDuration(this.timerPreset, false);
        }
        return '-';
      }

      return `${this.trialDataInfo} / ${this.durationFormatted}`;
    }, // trialBadge()

    totalDataInfo() {
      const trials = chain(this.data)
        .get(['data'])
        .filter({ target: get(this.target, 'id') })
        .filter('promptLevel')
        .value();

      const trialCount = size(trials);

      if (!this.hasMultiplePromptLevels) {
        return trialCount;
      }

      return this.calculateTrialsSummary(trials);
    }, // totalDataInfo()

    totalBadge() {
      if (this.isSessionTimerLinked) {
        return this.totalDataInfo;
      }

      if (!this.totalDuration) {
        return '';
      }
      return `${this.totalDataInfo} / ${this.formatDuration(this.totalDuration, false)}`;
    }, // totalBadge()

  },

  watch: {

    durationOffset: {
      immediate: true,
      handler(newDurationOffset, prevDurationOffset) {
        if (isDate(newDurationOffset) && isDate(prevDurationOffset)
        && (newDurationOffset.getTime() === prevDurationOffset.getTime())) {
          return;
        }
        if (newDurationOffset === null) {
          this.stopTimer();
        } else if (!this.isTimerStarted && !this.sessionPaused) {
          this.startTimer(newDurationOffset);
        }
      },
    },

    sessionPaused: {
      immediate: false,
      handler(sessionPausedState) {
        if (sessionPausedState) {
          if (!this.isTimerStarted) {
            return;
          }
          this.recordDuration(this.currentDuration);
          this.stopTimer();
        } else if (this.durationOffset !== null) {
          const timerOffset = this.sessionDurationOffset || new Date();
          this.recordDuration(this.currentDuration, timerOffset);
          this.startTimer(timerOffset);
        }
      },
    },
  },

  mounted() {
    this.initTargetData();
  },

  beforeUnmount() {
    cancelAnimationFrame(this.timerInterval);
  }, // mounted()

  methods: {
    getTextColor,

    /**
     * Called by parent to signal session finish
     */
    finishSession() {
      if (this.isTimerStarted) {
        const data = this.recordTimerEndData();
        this.stopTimer();
        if (!isEmpty(data)) {
          return [{
            target: this.target.id,
            ...data,
          }];
        }
      }
    },

    onAddMultipleTrials() {
      if (!this.targetIsOpen || (this.isTimerStarted && !this.isSessionTimerLinked)) {
        return;
      }

      this.openComponentDialog(AddMultipleTrials, {
        program: this.sessionProgram,
        // component needs only promptLevels array in the object
        promptLevelTemplate: { promptLevels: this.programPrompts },
        showDuration: !this.isSessionTimerLinked,
        duration: 0,
      }, ({ data, duration }) => {
        // manually entered duration of all selected promptLevels including the existing target duration
        // record data of each active promtLevel
        let trial = null;
        if (!this.isSessionTimerLinked) {
          const startData = this.recordTimerStartData();
          this.$emit('record', startData);
          trial = startData.trial;
        } else {
          trial = this.nextTrial;
        }
        data.forEach((activePromptLevel) => {
          const { promptLevel, value } = activePromptLevel;

          if (value > 0) {
            for (let i = 0; i < value; i += 1) {
              const dataObj = this.recordData(promptLevel, trial);
              this.$emit('record', dataObj);
              if (this.isSessionTimerLinked) {
                // If session timer is linked, we need to increment the trial number
                trial += 1;
              }
            }
          }
        });
        if (!this.isSessionTimerLinked) {
          const endData = this.recordTimerEndData(duration, trial);
          this.$emit('record', endData);
        }
      });
    }, // onAddMultipleTrials()

    onMouseDown() {
      if (!this.targetIsOpen || (!this.isTimerStarted && !this.isSessionTimerLinked)) {
        return;
      }

      this.stopIndicatorProgress = 0;
      const indicatorTimer = setInterval(() => {
        if (this.stopIndicatorProgress === null) {
          clearTimeout(indicatorTimer);
          return;
        }
        this.stopIndicatorProgress += 5;
        if (this.stopIndicatorProgress > 100) {
          this.onStopTimer();
          clearTimeout(indicatorTimer);
        }
      }, 50);
    },

    onMouseUp() {
      if (this.stopIndicatorProgress === null) {
        this.onStartTimer();
        return;
      }
      if (this.stopIndicatorProgress > 100) {
        this.stopIndicatorProgress = null;
      }

      this.stopIndicatorProgress = null;
    },

    onSelectTarget() {
      if (!this.targetIsOpen || (!this.isTimerStarted && !this.isSessionTimerLinked)) {
        return;
      }

      this.notifyDeletedPromptLevelTemplate();

      if (!this.hasMultiplePromptLevels) {
        // Only one prompt level - record right away
        const data = this.recordData(head(this.programPrompts));
        this.$emit('record', data);
        this.isSelected = false;
        return;
      }

      this.isSelected = true;
    }, // onSelectTarget()

    onSelectPromptLevel(promptLevel) {
      if (!this.targetIsOpen) {
        return;
      }
      const data = this.recordData(promptLevel);
      this.$emit('record', data);
    }, // onSelectPromptLevel()

    onStartTimer() {
      if (!this.targetIsOpen || this.isTimerStarted) {
        return;
      }
      this.isSelected = false;
      this.recordTimerStartSessionData();
      const data = this.recordTimerStartData();
      this.$emit('record', data);
    }, // onStartTimer()

    onStopTimer() {
      if (!this.targetIsOpen || !this.isTimerStarted) {
        return;
      }
      const data = this.recordTimerEndData();
      this.$emit('record', data);
      this.recordTimerEndSessionData();
    }, // onStopTimer()

    startTimer(timeOffset = null) {
      if (this.isTimerStarted) {
        // Prevent multiple simultanous timers
        return;
      }

      this.timerStart = timeOffset || new Date();

      const updateTimer = () => {
        const curTime = new Date();
        this.duration = (curTime.getTime() - this.timerStart.getTime()) / 1000;

        if (this.isCountDown && this.currentDuration >= this.timerPreset) {
          const data = this.recordTimerEndData();
          this.$emit('record', data);
          this.recordTimerEndSessionData();
          this.duration = 0;
          return;
        }

        this.timerInterval = requestAnimationFrame(updateTimer);
      };

      setTimeout(updateTimer);
    }, // startTimer()

    stopTimer() {
      if (!this.isTimerStarted) {
        return;
      }

      cancelAnimationFrame(this.timerInterval);
      this.timerInterval = null;
      this.timerStart = null;

      this.duration = 0;
    },

    recordTimerStartSessionData() {
      if (this.isSessionTimerLinked) {
        return;
      }
      const sessionData = {
        [`timerOffset_${this.target.id}`]: Timestamp.now(),
      };
      this.$emit(
        'record-program-data',
        this.sessionProgram,
        sessionData,
      );
    }, // recordTimerStartSessionData()

    recordTimerStartData() {
      if (this.isSessionTimerLinked) {
        return null;
      }
      const data = {
        trial: this.nextTrial,
      };
      return data;
    }, // recordTimerStartData()

    recordTimerEndSessionData() {
      if (this.isSessionTimerLinked) {
        return;
      }
      const sessionData = {
        [`timerOffset_${this.target.id}`]: null,
        [`timerDuration_${this.target.id}`]: 0,
      };
      this.$emit(
        'record-program-data',
        this.sessionProgram,
        sessionData,
      );
    }, // recordTimerEndSessionData()

    recordTimerEndData(manualDuration = null, overwriteTrial = null) {
      if (this.isSessionTimerLinked) {
        return null;
      }

      const trial = (overwriteTrial === null) ? this.currentTrial : overwriteTrial;

      let currentCountdownDuration = this.currentDuration;

      if (this.isCountDown) {
        currentCountdownDuration = Math.min(this.timerPreset, currentCountdownDuration);
      }

      const data = {
        trial,
        duration: manualDuration || currentCountdownDuration,
      };
      return data;
    }, // recordTimerEndData()

    recordData(promptLevel, overwriteTrial = null) {
      let trial = (overwriteTrial === null) ? this.currentTrial : overwriteTrial;
      if (this.isSessionTimerLinked && overwriteTrial === null) {
        trial = this.nextTrial;
      }
      const data = {
        promptLevel: get(promptLevel, 'id', null),
        scoreValue: get(promptLevel, 'score', null),
        trial,
      };
      const targetPromptLevelTemplate = this.getActivePromptLevelTemplateId(get(this.target, 'promptLevelTemplate'));

      if (targetPromptLevelTemplate) {
        Object.assign(data, { promptLevelTemplate: targetPromptLevelTemplate });
      }

      return data;
    }, // recordData()

    recordDuration(duration, timerOffsetTimestampDate = null) {
      const timerOffsetTimestamp = (timerOffsetTimestampDate !== null)
        ? Timestamp.fromDate(timerOffsetTimestampDate)
        : null;
      const dataObj = {
        [`timerDuration_${this.target.id}`]: duration,
      };
      if (timerOffsetTimestamp !== null) {
        dataObj[`timerOffset_${this.target.id}`] = timerOffsetTimestamp;
      }
      this.$emit('record-program-data', this.sessionProgram, dataObj);
    },

    initTargetData() {
      // Record zero's for session-timer linked targets
      if (!this.targetIsOpen || !this.isSessionTimerLinked) {
        return;
      }
      if (chain(this.data).get('data').find({ target: get(this.target, 'id') }).value()) {
        // Data already initialized
        return;
      }
      const data = { durationFromSession: true, phantomTrial: true };
      this.$emit('record', data);
    }, // initTargetData()

    undoData(undoDataItem) {
      const isOpeningRateTrial = ['time'].every((key) => key in undoDataItem)
        && !['duration', 'scoreValue', 'promptLevel'].some((key) => key in undoDataItem);

      if (isOpeningRateTrial && !this.isSessionTimerLinked) {
        this.stopTimer();

        const sessionData = {
          [`timerOffset_${this.target.id}`]: null,
        };

        this.$emit(
          'record-program-data',
          this.sessionProgram,
          sessionData,
        );
      }

      return true;
    },

  },

};
</script>
<style scoped>

.prompt-level {
  border-width: 0px 5px;
  border-style: solid;
}

.rate-data-overline {
  font-size: 9px;
  line-height:1rem;
}

.front-of-dimmed {
  z-index: 1;
}

</style>
